// 현재 웹 미리보기 — 실제 웹 앱을 iframe 으로 띄워 "아이가 지금 보는 화면"을 그대로 보여준다.
//
// 목업을 관리자에 다시 그리지 않는다(GoodsPreviewFrame 과 같은 원칙, CLAUDE.md — 렌더러 이원화 금지).
// 매니페스트 실물·번들 기본 이미지·절차적 캔버스가 합쳐진 "현재 상태"를 웹이 그대로 렌더한다.
import { useMemo, useState } from "react";
import { contentBase } from "../lib/contentApi";

interface Screen {
  key: string;
  label: string;
  icon: string;
  path: string; // 웹 라우트(+쿼리). world 는 ?zone= 딥링크.
  note: string;
}

// 웹 라우트(apps/web/src/lib/routes.ts)·존(zoneMeta) 과 일치시킬 것.
const SCREENS: Screen[] = [
  { key: "landing", label: "랜딩", icon: "🏠", path: "/", note: "메인 화면 — 무대 배경·캐릭터·하단 메뉴" },
  { key: "verse", label: "입장", icon: "🚪", path: "/v", note: "메타버스 입장 — 히어로 로드/즉석 생성" },
  { key: "lobby", label: "그림책 마을", icon: "🗺️", path: "/world?zone=lobby", note: "월드 시작점 — NPC 산책·포털·별 모으기" },
  { key: "wbook", label: "도서관", icon: "📚", path: "/world?zone=book", note: "액자→숏폼, 전자책 전체 보기" },
  { key: "gyeongju", label: "경주", icon: "🏯", path: "/world?zone=gyeongju", note: "오브젝트→카드(삽화·나레이션)·경주 랜드마크" },
  { key: "goods", label: "굿즈 마켓", icon: "🛍️", path: "/world?zone=goods", note: "진열대→굿즈 상세" },
  { key: "event", label: "이벤트 광장", icon: "🎁", path: "/world?zone=event", note: "화상 무대(M3)" },
  { key: "book", label: "전자책", icon: "📖", path: "/book", note: "그림책 뷰어 — 페이지·자막·음성" },
  { key: "ar", label: "AR", icon: "🪄", path: "/ar", note: "AR 켜진 페이지 목록(저장한 것). 실제 인식은 휴대폰 카메라로 그 페이지를 비춰야 동작" },
];

export function LivePreview() {
  const base = contentBase();
  const [screen, setScreen] = useState<Screen>(SCREENS[0]!);
  const [wide, setWide] = useState(false);
  const [nonce, setNonce] = useState(0); // 새로고침용 — 값이 바뀌면 iframe 재마운트
  const src = useMemo(() => `${base}${screen.path}`, [base, screen.path]);

  return (
    <section className="as-sec live">
      <div className="adminbar">
        <span className="adminbar__title">현재 웹 미리보기</span>
        <span className="adminbar__actions">
          <button type="button" className="btn" onClick={() => setWide((w) => !w)}>
            {wide ? "📱 모바일 폭" : "🖥️ 넓게"}
          </button>
          <button type="button" className="btn" onClick={() => setNonce((n) => n + 1)}>↻ 새로고침</button>
          <a className="btn" href={src} target="_blank" rel="noreferrer">↗ 새 탭에서 열기</a>
        </span>
      </div>

      <p className="console__hint">
        실제 웹 화면을 그대로 띄웁니다 — 업로드한 그림(실물)·번들 기본 그림·코드로 그린 배경이 합쳐진 <b>지금 상태</b>입니다.
        출처: <code>{base}</code> (웹 dev 서버가 떠 있어야 보입니다).
      </p>

      <div className="live__chips">
        {SCREENS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`live__chip${screen.key === s.key ? " on" : ""}`}
            onClick={() => setScreen(s)}
          >
            <span aria-hidden="true">{s.icon}</span> {s.label}
          </button>
        ))}
      </div>
      <p className="live__note">{screen.icon} <b>{screen.label}</b> — {screen.note}</p>

      <div className={`live__stage${wide ? " live__stage--wide" : ""}`}>
        <iframe
          key={`${screen.key}-${nonce}`}
          className="live__frame"
          src={src}
          title={`웹 미리보기 · ${screen.label}`}
          loading="lazy"
          allow="camera; microphone" // AR 미리보기에서 카메라 요청 허용(실인식은 실기기 필요)
        />
      </div>
    </section>
  );
}
